import { Button } from "@capacit-com/lara-law-shared-ui";
import {
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@capacit-com/lara-law-shared-ui";
import { Input } from "@capacit-com/lara-law-shared-ui";
import { Textarea } from "@capacit-com/lara-law-shared-ui";
import Prompt, { PromptStatus } from "@/models/prompt/prompt";
import MultiSelectField from "@/pages/chat/components/filter/MultiSelectField";
import { useFormik } from "formik";
import { Save, X } from "lucide-react";
import { FC, useEffect, useState } from "react";
import { PromptSchemaType } from "@/pages/chat/components/prompt-library/PromptLibraryForm";
import * as Yup from "yup";
import { TagType } from "@/models/tag/tag";
import { CreateTagPopover } from "./CreateTagPopover";
import usePromptsCrud from "../hooks/usePromptsCrud";
import { Spinner } from "@capacit-com/lara-law-shared-ui";
import { Permission } from "@/lib/permissions/Permission.enum";
import { useHasPermission } from "@/lib/permissions/permissionFunctions";

type EditPromptFormProps = {
  selectedPrompt: Prompt;
  handleBack: () => void;
  setFormHasChanged: (hasChanged: boolean) => void;
};

const EditPromptForm: FC<EditPromptFormProps> = ({
  selectedPrompt,
  handleBack,
  setFormHasChanged,
}) => {
  const hasPermission = useHasPermission();
  const { tags, handleCreateTag, handleUpdatePrompt } = usePromptsCrud();

  const [initialPrompt] = useState<PromptSchemaType>({
    title: selectedPrompt?.title || "",
    description: selectedPrompt?.description || "",
    promptString: selectedPrompt?.promptString || "",
    tags: selectedPrompt?.tags || [],
    status: selectedPrompt?.status || PromptStatus.Personal,
  });

  const validationSchema = Yup.object().shape({
    title: Yup.string()
      .max(50, "Titel må højst være 50 tegn") // Yup validation for max length
      .required("Titel er påkrævet"),
    description: Yup.string()
      .max(450, "Beskrivelse må højst være 450 tegn")
      .required("Beskrivelse er påkrævet"),
    promptString: Yup.string().required("Prompt er påkrævet"),
    tags: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().required("Tag-navn er påkrævet"),
        tagType: Yup.mixed<TagType>()
          .oneOf(Object.values(TagType) as TagType[])
          .required("Tag-type er påkrævet"),
      })
    ),
    status: Yup.mixed<PromptStatus>()
      .oneOf(Object.values(PromptStatus) as PromptStatus[])
      .required("Prompt-status er påkrævet"),
  });

  const formik = useFormik({
    initialValues: initialPrompt,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      values.title = values.title.trim();
      values.description = values.description.trim();
      values.promptString = values.promptString.trim();

      if (await handleUpdatePrompt(selectedPrompt.id, values)) {
        handleBack();
      }
    },
  });

  useEffect(() => {
    const hasChanged =
      JSON.stringify(formik.values) !== JSON.stringify(initialPrompt);
    setFormHasChanged(hasChanged);
  }, [formik.values, initialPrompt, setFormHasChanged]);

  return (
    <>
      <div className="flex flex-col gap-2 flex-1 min-h-0">
        <DialogHeader className="flex-none text-2xl font-semibold">
          <DialogTitle>Rediger prompt: {selectedPrompt.title}</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={formik.handleSubmit}
          className="flex flex-col flex-1 min-h-0"
        >
          <div className="flex flex-col gap-2 flex-1 min-h-0">
            <div className="flex flex-col gap-2">
              <div className="flex justify-between">
                <label htmlFor="title" className="text-sm font-medium">
                  Title<span className="text-destructive">*</span>
                </label>
                <p className="text-xs font-medium">
                  {formik.values.title.length}/50
                </p>
              </div>
              <Input
                name="title"
                placeholder="Titel... (max 50 tegn)"
                value={formik.values.title}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                maxLength={50}
              />
              {formik.errors.title && formik.touched.title && (
                <div className="text-destructive text-xs">
                  {formik.errors.title}
                </div>
              )}
            </div>

            <div className="flex justify-between gap-2 mb-1">
              {hasPermission({ allOf: [Permission.CreateTag] }) ? (
                <div className="w-[49%] flex flex-col relative">
                  <div className="space-y-2">
                    <MultiSelectField
                      label="Afdeling"
                      onClear={() => {
                        formik.setFieldValue(
                          "tags",
                          formik.values.tags.filter(
                            (tag) => tag.tagType !== TagType.Department
                          )
                        );
                      }}
                      placeholder="Vælg afdeling"
                      options={tags.filter(
                        (tag) => tag.tagType === TagType.Department
                      )}
                      value={formik.values.tags.filter(
                        (tag) => tag.tagType === TagType.Department
                      )}
                      onChange={(option) => {
                        const currentArray = [...formik.values.tags];

                        if (
                          currentArray.some((tag) => tag.name === option.name)
                        ) {
                          currentArray.splice(
                            currentArray.findIndex(
                              (tag) => tag.name === option.name
                            ),
                            1
                          );
                        } else {
                          if (
                            currentArray.some(
                              (tag) => tag.tagType === TagType.Department
                            )
                          ) {
                            currentArray.splice(
                              currentArray.findIndex(
                                (tag) => tag.tagType === TagType.Department
                              ),
                              1
                            );
                          }
                          currentArray.push(option);
                        }
                        formik.setFieldValue("tags", currentArray);
                      }}
                      optionKey="name"
                    />
                  </div>
                  <div className="absolute bottom-0 right-0">
                    <CreateTagPopover
                      onCreateTag={handleCreateTag}
                      tagType={TagType.Department}
                    />
                  </div>
                </div>
              ) : (
                <div className="w-[49%] flex flex-col space-y-2">
                  <MultiSelectField
                    label="Afdeling"
                    onClear={() => {
                      formik.setFieldValue(
                        "tags",
                        formik.values.tags.filter(
                          (tag) => tag.tagType !== TagType.Department
                        )
                      );
                    }}
                    placeholder="Vælg afdeling"
                    options={tags.filter(
                      (tag) => tag.tagType === TagType.Department
                    )}
                    value={formik.values.tags.filter(
                      (tag) => tag.tagType === TagType.Department
                    )}
                    onChange={(option) => {
                      const currentArray = [...formik.values.tags];

                      if (
                        currentArray.some((tag) => tag.name === option.name)
                      ) {
                        currentArray.splice(
                          currentArray.findIndex(
                            (tag) => tag.name === option.name
                          ),
                          1
                        );
                      } else {
                        if (
                          currentArray.some(
                            (tag) => tag.tagType === TagType.Department
                          )
                        ) {
                          currentArray.splice(
                            currentArray.findIndex(
                              (tag) => tag.tagType === TagType.Department
                            ),
                            1
                          );
                        }
                        currentArray.push(option);
                      }
                      formik.setFieldValue("tags", currentArray);
                    }}
                    optionKey="name"
                  />
                </div>
              )}

              {hasPermission({ allOf: [Permission.CreateTag] }) ? (
                <div className="w-[49%] flex flex-col relative">
                  <div className="space-y-2">
                    <MultiSelectField
                      label="Tags"
                      onClear={() => {
                        formik.setFieldValue(
                          "tags",
                          formik.values.tags.filter(
                            (tag) => tag.tagType !== TagType.Tag
                          )
                        );
                      }}
                      placeholder="Vælg tags"
                      options={tags.filter(
                        (tag) => tag.tagType === TagType.Tag
                      )}
                      value={formik.values.tags.filter(
                        (tag) => tag.tagType === TagType.Tag
                      )}
                      onChange={(option) => {
                        const currentArray = [...formik.values.tags];

                        if (
                          currentArray.some((tag) => tag.name === option.name)
                        ) {
                          currentArray.splice(
                            currentArray.findIndex(
                              (tag) => tag.name === option.name
                            ),
                            1
                          );
                        } else {
                          if (
                            currentArray.filter(
                              (tag) => tag.tagType === TagType.Tag
                            ).length === 4
                          ) {
                            return;
                          }
                          currentArray.push(option);
                        }
                        formik.setFieldValue("tags", currentArray);
                      }}
                      optionKey="name"
                    />
                  </div>
                  <div className="absolute bottom-0 right-0">
                    <CreateTagPopover
                      onCreateTag={handleCreateTag}
                      tagType={TagType.Tag}
                    />
                  </div>
                </div>
              ) : (
                <div className="w-[49%] flex flex-col space-y-2">
                  <MultiSelectField
                    label="Tags"
                    onClear={() => {
                      formik.setFieldValue(
                        "tags",
                        formik.values.tags.filter(
                          (tag) => tag.tagType !== TagType.Tag
                        )
                      );
                    }}
                    placeholder="Vælg tags"
                    options={tags.filter((tag) => tag.tagType === TagType.Tag)}
                    value={formik.values.tags.filter(
                      (tag) => tag.tagType === TagType.Tag
                    )}
                    onChange={(option) => {
                      const currentArray = [...formik.values.tags];

                      if (
                        currentArray.some((tag) => tag.name === option.name)
                      ) {
                        currentArray.splice(
                          currentArray.findIndex(
                            (tag) => tag.name === option.name
                          ),
                          1
                        );
                      } else {
                        if (
                          currentArray.filter(
                            (tag) => tag.tagType === TagType.Tag
                          ).length === 4
                        ) {
                          return;
                        }
                        currentArray.push(option);
                      }
                      formik.setFieldValue("tags", currentArray);
                    }}
                    optionKey="name"
                  />
                </div>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex justify-between">
                <label htmlFor="description" className="text-sm font-medium">
                  Beskriv formålet med prompten
                  <span className="text-destructive">*</span>
                </label>
                <p className="text-xs font-medium">
                  {formik.values.description.length}/450
                </p>
              </div>
              <Textarea
                name="description"
                placeholder="Med dette prompt kan du lave et referat af en entrepriseaftale (maksimalt 450 tegn)"
                value={formik.values.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                maxLength={450}
                rows={4}
              />
              {formik.errors.description && formik.touched.description && (
                <div className="text-destructive text-xs">
                  {formik.errors.description}
                </div>
              )}
            </div>
            <div className="flex flex-col gap-2 flex-1 min-h-0">
              <label htmlFor="promptString" className="text-sm font-medium">
                Prompt
              </label>
              <Textarea
                name="promptString"
                placeholder="Prompt..."
                value={formik.values.promptString}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="min-h-20 flex mb-6"
                rows={6}
              />
              {formik.errors.promptString && formik.touched.promptString && (
                <div className="text-destructive text-xs">
                  {formik.errors.promptString}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button size={"sm"} type="submit" disabled={formik.isSubmitting}>
              {formik.isSubmitting ? (
                <>
                  <Spinner className="text-primary-foreground" />
                  Gemmer...
                </>
              ) : (
                <>
                  <Save />
                  Gem
                </>
              )}
            </Button>
            <Button
              className="text-destructive-foreground bg-destructive hover:bg-destructive/90"
              size={"sm"}
              onClick={handleBack}
            >
              <X />
              Annuller
            </Button>
          </DialogFooter>
        </form>
      </div>
    </>
  );
};

export default EditPromptForm;
